import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import Layout, { Protected } from './components/Layout';
import { SkeletonPage } from './components/ui';
import LoginPage from './pages/Login';
import ProjectListPage from './pages/ProjectList';
import ProjectNewPage from './pages/ProjectNew';
import ProjectDetailPage from './pages/ProjectDetail';
import MemberPermsPage from './pages/MemberPerms';
import InboxPage from './pages/Inbox';
import RequestDetailPage from './pages/RequestDetail';
import AdminPage from './pages/Admin';

// 비용 대시보드는 echarts를 끌어오므로 코드 분할(지연 로딩).
const CostAllProjectsPage = lazy(() => import('./pages/costs/CostAllProjects'));
const CostByUserPage = lazy(() => import('./pages/costs/CostByUser'));
const CostSettingsPage = lazy(() => import('./pages/costs/CostSettings'));
// 데이터 카탈로그도 별도 청크로 분할.
const DataCatalogPage = lazy(() => import('./pages/catalog/DataCatalog'));
const MonitoringPage = lazy(() => import('./pages/Monitoring'));
const BqSlotsPage = lazy(() => import('./pages/BqSlots'));
const ApiTokensPage = lazy(() => import('./pages/ApiTokens'));
const ApiGuidePage = lazy(() => import('./pages/ApiGuide'));
const BqAssetsPage = lazy(() => import('./pages/bqAssets/BqAssets'));
const CpeSimulatorPage = lazy(() => import('./pages/cpe/CpeSimulator'));
// 네트워크 관리 — 네트워크 메타데이터 목록 + 상세.
const NetworkMetaPage = lazy(() => import('./pages/networkMeta/NetworkMetaPage'));
const NetworkNewPage = lazy(() => import('./pages/networkMeta/NetworkNewPage'));
// 리셀러 허용 — 리셀러 계정별로 볼 수 있는 리셀러(BigQuery 정본) 편집.
const ResellerAllowedPage = lazy(() => import('./pages/resellerAllowed/ResellerAllowedPage'));
const MaxControlPage = lazy(() => import('./pages/maxControl/MaxControlPage'));

// M3 라우트 트리.
// /                       → ProjectList     (모두)
// /projects/new           → ProjectNew      (admin/member — 비member도 요청 자체는 가능)
// /projects/:project_id   → ProjectDetail   (모두) — 5자리 숫자 ID (utils.apps.project_id)
// /members                → MemberPerms     (admin/member)
// /inbox             → Inbox           (admin: 전체 pending, member: 본인 요청)
// /inbox/:requestId  → RequestDetail   (admin: 결정, member: 본인 요청 조회)
// /admin             → Admin           (admin only)
export default function App() {
  return (
    <Routes>
      {/* /login 은 Layout 바깥 — 인증 전이라 사이드바/topbar 불필요. */}
      <Route path="/login" element={<LoginPage />} />
      <Route element={<Layout />}>
        <Route path="/" element={<ProjectListPage />} />
        <Route path="/projects/new" element={<ProjectNewPage />} />
        <Route path="/projects/:project_id" element={<ProjectDetailPage />} />
        <Route
          path="/cpe"
          element={
            <Protected allow={['admin', 'leader', 'member', 'none']} reason="로그인이 필요해요">
              <Suspense fallback={<SkeletonPage />}>
                <CpeSimulatorPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/members"
          element={
            <Protected allow={['admin', 'leader', 'member', 'none']} reason="로그인이 필요해요">
              <MemberPermsPage />
            </Protected>
          }
        />
        <Route
          path="/inbox"
          element={
            <Protected allow={['admin', 'leader', 'member', 'none']} reason="로그인이 필요해요">
              <InboxPage />
            </Protected>
          }
        />
        <Route
          path="/inbox/:requestId"
          element={
            <Protected allow={['admin', 'leader', 'member', 'none']} reason="로그인이 필요해요">
              <RequestDetailPage />
            </Protected>
          }
        />
        <Route
          path="/admin"
          element={
            <Protected allow={['admin']} reason="Admin만 사용자 권한을 관리할 수 있어요">
              <AdminPage />
            </Protected>
          }
        />
        <Route
          path="/monitoring"
          element={
            <Protected allow={['admin']} menu="monitoring" reason="인프라 모니터 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <MonitoringPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/monitoring/bq-slots"
          element={
            <Protected allow={['admin']} menu="monitoring_bq_slots" reason="빅쿼리 슬롯 모니터링 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <BqSlotsPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/api-tokens"
          element={
            <Protected allow={['admin']} menu="api_tokens" reason="API 토큰 메뉴 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <ApiTokensPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/api-guide"
          element={
            <Protected allow={['admin']} menu="api_guide" reason="API 가이드 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <ApiGuidePage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/catalog"
          element={
            <Protected allow={['admin', 'leader', 'member', 'none']} reason="로그인이 필요해요">
              <Suspense fallback={<SkeletonPage />}>
                <DataCatalogPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/costs/all-projects"
          element={
            <Protected allow={['admin']} menu="costs" reason="비용 대시보드 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <CostAllProjectsPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/costs/users"
          element={
            <Protected allow={['admin']} menu="costs_users" reason="비용 대시보드 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <CostByUserPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/costs/settings"
          element={
            <Protected allow={['admin']} menu="costs_settings" reason="비용 설정 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <CostSettingsPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/bq-assets"
          element={
            <Protected allow={['admin']} menu="bq_assets" reason="BigQuery 미사용 테이블 관리 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <BqAssetsPage />
              </Suspense>
            </Protected>
          }
        />

        <Route
          path="/networks"
          element={
            <Protected allow={['admin']} menu="network_meta" reason="Admon 네트워크 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <NetworkMetaPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/networks/new"
          element={
            <Protected allow={['admin']} menu="network_meta" reason="Admon 네트워크 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <NetworkNewPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/networks/resellers"
          element={
            <Protected allow={['admin']} menu="reseller_allowed" reason="리셀러 허용 접근 권한이 없어요">
              <Suspense fallback={<SkeletonPage />}>
                <ResellerAllowedPage />
              </Suspense>
            </Protected>
          }
        />
        <Route path="/growth/max-instance-control/*" element={
          <Protected allow={['admin']} menu="network_meta" reason="MAX 긴급 차단 접근 권한이 없어요">
            <Suspense fallback={<SkeletonPage />}><MaxControlPage /></Suspense>
          </Protected>
        } />
        <Route path="/networks/instance-control/*" element={<RedirectLegacyMaxControl />} />
        {/* 상세는 별도 페이지가 아니라 목록 오른쪽 패널이 됐다 — 옛 링크를 살려 둔다. */}
        <Route path="/networks/:scNaming" element={<RedirectLegacyNetwork />} />

        {/* 레거시 redirect */}
        <Route path="/projects" element={<Navigate to="/" replace />} />
        <Route path="/requests" element={<Navigate to="/inbox" replace />} />
        <Route path="/requests/:requestId" element={<RedirectLegacyRequest />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function RedirectLegacyMaxControl() {
  const tail = useParams()['*'];
  const location = useLocation();
  return <Navigate to={`/growth/max-instance-control${tail ? `/${tail}` : ''}${location.search}${location.hash}`} replace />;
}

// /networks/<sc_naming> → /networks?network=<sc_naming>. 상세가 페이지에서 패널이 됐다.
function RedirectLegacyNetwork() {
  const segments = window.location.pathname.split('/');
  const scNaming = segments[segments.length - 1];
  return <Navigate to={`/networks?network=${encodeURIComponent(scNaming)}`} replace />;
}

function RedirectLegacyRequest() {
  const segments = window.location.pathname.split('/');
  const id = segments[segments.length - 1];
  return <Navigate to={`/inbox/${id}`} replace />;
}
